import React from "react";
import { Text, View, StyleSheet } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";
import { formatTaskAndRiskId } from "src/modules/data-protection/constants";

const styles = StyleSheet.create({
    section: {
        margin: 10,
        padding: 20,
        paddingTop: 0,
        paddingBottom: 0
    },
    sectionTitle: {
        fontWeight: 800,
        fontSize: 13,
        marginBottom: 12
    },
    sectionBody: {
        margin: 0,
        padding:0
    },
    pText: {
        fontWeight: 400,
        marginBottom: 12,
        fontSize: 11,
    },
    fontBold: {
        fontWeight: 'bold'
    },
    table: {
        display: "table",
        width: "auto",
        marginBottom: '10px',
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: "#000",
    },
    tableRow: {
        flexDirection: "row",
        borderBottom: "1px solid #000"
    },
    tableLastRow : {
        borderBottom: 'none'
    },
    tableColHeader: {
        width: "50%",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        backgroundColor: "#eeeeee",
        borderRight: '1px solid #000'
    },
    tableCol: {
        width: "50%",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        borderRight: '1px solid #000',
        paddingTop: 2,
        paddingBottom: 2
    },
    tableCell: {
        margin: 5,
        fontSize: 8,
        maxWidth: "100%", 
        wordWrap: "break-word",
        whiteSpace: "normal",
        overflow: "hidden"
    },
    coordsCell: {
        margin: 5,
        fontSize: 8,
        backgroundColor: 'red'
    },
    borderRightNone: {
        borderRight: 'none'
    },
    list: {
        marginVertical: 4,
        marginLeft: 12,
    },
    listItem: {
        flexDirection: "row",
        marginBottom: 4,
    },
    bullet: {
        width: 10,
        fontSize: 12,
    },
    listText: {
        flex: 1,
        fontWeight: 400,
        fontSize: 11,
    },
    detailsBlock: {
        borderLeft: "1px solid #000",
        borderRight: "1px solid #000",
        borderBottom: "1px solid #000",
        padding: 6,
        fontSize: 8,
      },
      detailsLabel: {
        fontWeight: 600,
        fontSize: 8,
        marginTop: 3,
      },
      detailsText: {
        fontWeight: 400,
        fontSize: 8,
        marginTop: 1,
      },
});

const TableHeader = ({ t }) => {
    return (
        <View style={styles.tableRow}>
            <View style={styles.tableColHeader}>
                <Text style={{
                    ...styles.tableCell,
                    ...styles.fontBold
                }}>ID</Text>
            </View>

            <View style={styles.tableColHeader}>
                <Text style={{
                    ...styles.tableCell,
                    ...styles.fontBold
                }}>{t('Controls according to C5:2020 (Cloud Computing Compliance Controls Catalogue)')}</Text>
            </View>

            <View style={styles.tableColHeader}>
                <Text style={{
                    ...styles.tableCell,
                    ...styles.fontBold
                }}>
                    {t('Applicable(YES/NO)')}
                </Text>
            </View>

            <View style={styles.tableColHeader}>
                <Text style={{
                    ...styles.tableCell,
                    ...styles.fontBold
                }}>{t('Justification for selection/ exclusion')}</Text>
            </View>

            <View style={styles.tableColHeader}>
                <Text style={{
                    ...styles.tableCell,
                    ...styles.fontBold
                }}>{t('Implementation method')}</Text>
            </View>

            <View style={{
                ...styles.tableColHeader,
                ...styles.borderRightNone
            }}>
                <Text style={{
                    ...styles.tableCell,
                    ...styles.fontBold
                }}>{t('Status')}</Text>
            </View>
        </View>
    )
}

const TableRow = ({ t, control, index }) => {      
    const rowStyle = {
        ...styles.tableRow,
        ...(((index + 1) >= 14) && { ...styles.tableLastRow })
    }

    return (
        <>
          {/* Row: ONLY columns */}
          <View style={rowStyle} wrap={false}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>{control.controlNumber}</Text>
            </View>
      
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>{t(control.title)}</Text>
            </View>
      
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>{t(control.applicable ? "Yes" : "No")}</Text>
            </View>
      
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>
                {control.notApplicableReason || ""}
                {control.notApplicableReason && control.risks?.length > 0 ? ", " : ""}
                {(control.risks || []).map(
                  (
                    { asset_id, case_secondary_id, secondary_id, is_mitigation, questionnaire },
                    pIndex
                  ) => (
                    <Text key={pIndex}>
                      {formatTaskAndRiskId(
                        "risk",
                        questionnaire === "software" ? asset_id : case_secondary_id,
                        secondary_id,
                        is_mitigation,
                        questionnaire,
                        null
                      )}
                      {pIndex < control.risks.length - 1 ? ", " : ""}
                    </Text>
                  )
                )}
              </Text>
            </View>
      
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>
                {(control.documents || []).map((document, pIndex) => (
                  <Text key={pIndex}>
                    {document.title}
                    {pIndex < control.documents.length - 1 ? ", " : ""}
                  </Text>
                ))}
              </Text>
            </View>
      
            <View style={{ ...styles.tableCol, ...styles.borderRightNone }}>
              <Text style={styles.tableCell}>
                {t(control.applicable ? "Implemented" : "")}
              </Text>
            </View>
          </View>
      
          {/* Details: FULL WIDTH under the row */}
          <View style={{ ...styles.detailsBlock, marginTop: -1 }} wrap>
            {!!control.description && (
              <>
                <Text style={styles.detailsLabel}>{t("Description")}:</Text>
                <Text style={styles.detailsText}>{control.description}</Text>
              </>
            )}
      
            {!!control.additionalCriterion && (
              <>
                <Text style={styles.detailsLabel}>{t("Additional criterion")}:</Text>
                <Text style={styles.detailsText}>{control.additionalCriterion}</Text>
              </>
            )}
      
            {!!control.supplementaryInformation && (
              <>
                <Text style={styles.detailsLabel}>{t("Supplementary information")}:</Text>
                <Text style={styles.detailsText}>{control.supplementaryInformation}</Text>
              </>
            )}
      
            {!!control.continuousAuditingFeasibility && (
              <>
                <Text style={styles.detailsLabel}>{t("Continuous auditing feasibility")}:</Text>
                <Text style={styles.detailsText}>{control.continuousAuditingFeasibility}</Text>
              </>
            )}
      
            {!!control.continuousAuditingDescription && (
              <>
                <Text style={styles.detailsLabel}>{t("Continuous auditing description")}:</Text>
                <Text style={styles.detailsText}>{control.continuousAuditingDescription}</Text>
              </>
            )}
          </View>
        </>
      );

    // return (
    //     <View style={rowStyle}>
    //         <View style={styles.tableCol}>
    //             <Text style={styles.tableCell}>
    //                 {`${control.controlNumber}`}
    //             </Text>
    //         </View>

    //         <View style={styles.tableCol}>
    //                 <Text style={styles.tableCell}>
    //                     {t(control.title)}
    //                 </Text>
    //         </View>
    //                 {/* C5 extra fields under the title (NOT columns) */}
    //                 <View style={styles.detailsBlock} wrap>
    //     {!!control.description && (
    //       <>
    //         <Text style={styles.detailsLabel}>{t("Description")}:</Text>
    //         <Text style={styles.detailsText}>{control.description}</Text>
    //       </>
    //     )}

    //     {!!control.additionalCriterion && (
    //       <>
    //         <Text style={styles.detailsLabel}>{t("Additional criterion")}:</Text>
    //         <Text style={styles.detailsText}>{control.additionalCriterion}</Text>
    //       </>
    //     )}

    //     {!!control.supplementaryInformation && (
    //       <>
    //         <Text style={styles.detailsLabel}>{t("Supplementary information")}:</Text>
    //         <Text style={styles.detailsText}>{control.supplementaryInformation}</Text>
    //       </>
    //     )}

    //     {!!control.continuousAuditingFeasibility && (
    //       <>
    //         <Text style={styles.detailsLabel}>{t("Continuous auditing feasibility")}:</Text>
    //         <Text style={styles.detailsText}>{control.continuousAuditingFeasibility}</Text>
    //       </>
    //     )}

    //     {!!control.continuousAuditingDescription && (
    //       <>
    //         <Text style={styles.detailsLabel}>{t("Continuous auditing description")}:</Text>
    //         <Text style={styles.detailsText}>{control.continuousAuditingDescription}</Text>
    //       </>
    //     )}
    //   </View>


    //         <View style={styles.tableCol}>
    //             <Text style={styles.tableCell}>
    //                 {t(control.applicable ? 'Yes' : 'No')}
    //             </Text>
    //         </View>

    //         <View style={styles.tableCol}>
    //             <Text style={styles.tableCell}>
    //                 <Text>
    //                     {control.notApplicableReason || ""}
    //                 </Text>

    //                 {control.notApplicableReason && control.risks?.length > 0 && (
    //                     <Text>{', '}</Text>
    //                 )}
                    
    //                 <Text>
    //                     {(control.risks || []).map(({
    //                         asset_id,
    //                         case_secondary_id,
    //                         secondary_id,
    //                         is_mitigation,
    //                         questionnaire
    //                     }, pIndex) => {
    //                         return (
    //                             <Text key={pIndex}>
    //                                 {formatTaskAndRiskId(
    //                                     'risk', 
    //                                     questionnaire === "software" ? asset_id : case_secondary_id, 
    //                                     secondary_id, 
    //                                     is_mitigation, 
    //                                     questionnaire, 
    //                                     null
    //                                 )}

    //                                 {pIndex < (control.risks?.length - 1) ? ', ' : ''}
    //                             </Text>
    //                         )
    //                     })}
    //                 </Text>
    //             </Text>
    //         </View>

    //         <View style={styles.tableCol}>
    //             <Text style={styles.tableCell}>
    //                 <Text>
    //                     {(control.documents || []).map((document, pIndex) => {
    //                         return (
    //                             <Text key={pIndex}>
    //                                 {document.title}
    //                                 {pIndex < (control.documents?.length - 1) ? ', ' : ''}
    //                             </Text>
    //                         )
    //                     })}
    //                 </Text>
    //             </Text>
    //         </View>

    //         <View style={{
    //             ...styles.tableCol,
    //             ...styles.borderRightNone
    //         }}>
    //             <Text style={styles.tableCell}>
    //                 {t(control.applicable ? 'Implemented' : '')}
    //             </Text>
    //         </View>
    //     </View>
    // )
}

const PageContent = (props) => {
    const { controls, organization, t } = props;

    const rowsPerPage = 14;
    const pages = [];

    for (let i = 0; i < controls.length; i += rowsPerPage) {
        pages.push(controls.slice(i, i + rowsPerPage));
    }

    return (
        <>
            <View style={styles.section}>
                <Text style={styles.sectionTitle}>
                    {`1. ${t('Purpose, scope and users')}`}
                </Text>

                <View style={styles.sectionBody}>
                    <Text style={styles.pText}>
                        {t('The purpose of this document is to define which security controls are to be implemented in {{company}}.', {
                            'company': organization.name
                        })}
                    </Text>

                    <Text style={styles.pText}>
                        {t("This document includes the C5:2020 criteria selected for the scope of this organization. Applicability is assessed for the defined organizational scope and recorded below.")}
                    </Text>

                    <Text style={styles.pText}>
                        {t("Users of this document are all employees of {{company}} involved in implementing and maintaining the C5:2020 controls within scope.", {
                            'company': organization.name
                        })}
                    </Text>
                </View>
            </View>

            <View style={styles.section}>
                <Text style={styles.sectionTitle}>
                    {`2. ${t('Reference documents')}`}
                </Text>

                <View style={styles.sectionBody}>
                    <View style={styles.list}>
                        <View style={styles.listItem}>
                            <Text style={styles.bullet}>•</Text>
                            <Text style={styles.listText}>
                                {t('C5:2020 (Cloud Computing Compliance Controls Catalogue)')}
                            </Text>
                        </View>

                        <View style={styles.listItem}>
                            <Text style={styles.bullet}>•</Text>
                            <Text style={styles.listText}>
                                {t('Information Security Policy')}
                            </Text>
                        </View>

                        <View style={styles.listItem}>
                            <Text style={styles.bullet}>•</Text>
                            <Text style={styles.listText}>
                                {t('Risk Assessment and Risk Treatment Methodology')}
                            </Text>
                        </View>

                        <View style={styles.listItem}>
                            <Text style={styles.bullet}>•</Text>
                            <Text style={styles.listText}>
                                {t('Risk Assessment and Risk Treatment Report')}
                            </Text>
                        </View>
                    </View>
                </View>
            </View>

            <View style={styles.section}>
                <Text style={styles.sectionTitle}>
                    {`3. ${t('Applicability of controls')}`}
                </Text>

                <View style={styles.sectionBody}>
                    <Text style={styles.pText}>
                        {t('The following C5:2020 criteria are applicable:')}
                    </Text>
                    <View style={{ display: "flex" }}>
                        {pages.map((pageRows, pageIndex) => {
                            return (
                                <View style={styles.table} key={pageIndex} wrap={true} break>
                                    <TableHeader t={t} />
                                    {pageRows.map((control, cIndex) => (
                                        <TableRow
                                            key={control.id ?? cIndex}
                                            index={cIndex}
                                            control={control}
                                            t={t}
                                        />
                                    ))}
                                </View>
                            )
                        })}
                    </View>
                </View>
            </View>
        </>
    );
};

export default withNamespaces()(PageContent);
